/**
 * Framework-agnostic mount for React / Vue / vanilla hosts.
 * Uses Svelte 5 `mount` / `unmount`.
 */
import { mount, unmount, type Component } from "svelte";
import GpuTabs from "./components/GpuTabs.svelte";
import type { GpuPluginHost } from "./host.js";
import type { ConnectionTab } from "./connections.js";

export type GpuMountHandle = {
  update: (props: Record<string, unknown>) => void;
  destroy: () => void;
};

function makeHandle(
  target: HTMLElement,
  Comp: Component,
  initial: Record<string, unknown>,
): GpuMountHandle {
  let current = { ...initial };
  let app = mount(Comp, {
    target,
    props: current as never,
  });

  return {
    update(props) {
      current = { ...current, ...props };
      unmount(app);
      target.replaceChildren();
      app = mount(Comp, {
        target,
        props: current as never,
      });
    },
    destroy() {
      try {
        unmount(app);
      } catch {
        /* already gone */
      }
      target.replaceChildren();
    },
  };
}

export function mountGpuTabs(
  target: HTMLElement,
  opts: {
    host: GpuPluginHost;
    tab?: ConnectionTab;
    defaultTab?: ConnectionTab;
    showTabBar?: boolean;
    onTabChange?: (tab: ConnectionTab) => void;
  },
): GpuMountHandle {
  return makeHandle(target, GpuTabs, {
    host: opts.host,
    tab: opts.tab,
    defaultTab: opts.defaultTab ?? "cloudflare",
    showTabBar: opts.showTabBar ?? true,
    onTabChange: opts.onTabChange,
  });
}
